import type { OfflineCachePolicySettings } from "../api/types";

const HOUR_MS = 60 * 60 * 1000;
const MAX_TTL_HOURS = 336;

export type OfflineCachePolicy = {
  defaultTtlMs: number;
  referenceTtlMs: number;
  documentTtlMs: number;
  facilityTtlMs: number;
  locationTtlMs: number;
  metaTtlMs: number;
};

export type OfflineCachePolicyKey = keyof OfflineCachePolicy;

const DEFAULT_HOURS: OfflineCachePolicySettings = {
  defaultTtlHours: 12,
  referenceTtlHours: 24,
  documentTtlHours: 12,
  facilityTtlHours: 24,
  locationTtlHours: 24,
  metaTtlHours: 12,
};

const clamp = (value: number): number => Math.max(1, Math.min(MAX_TTL_HOURS, Math.trunc(value)));

const parseHours = (value: unknown, fallback: number): number => {
  if (typeof value === "number" && Number.isFinite(value)) {
    return clamp(value);
  }
  if (typeof value === "string" && value.trim().length > 0) {
    const parsed = Number.parseInt(value, 10);
    if (Number.isFinite(parsed)) {
      return clamp(parsed);
    }
  }
  return clamp(fallback);
};

const buildPolicy = (hours?: Partial<OfflineCachePolicySettings>): OfflineCachePolicy => {
  const source = hours ?? {};
  const defaultHours = parseHours(source.defaultTtlHours, DEFAULT_HOURS.defaultTtlHours);
  const referenceHours = parseHours(source.referenceTtlHours, DEFAULT_HOURS.referenceTtlHours ?? defaultHours);
  const documentHours = parseHours(source.documentTtlHours, DEFAULT_HOURS.documentTtlHours ?? defaultHours);
  const facilityHours = parseHours(source.facilityTtlHours, DEFAULT_HOURS.facilityTtlHours ?? defaultHours);
  const locationHours = parseHours(source.locationTtlHours, DEFAULT_HOURS.locationTtlHours ?? defaultHours);
  const metaHours = parseHours(source.metaTtlHours, DEFAULT_HOURS.metaTtlHours ?? defaultHours);
  return {
    defaultTtlMs: defaultHours * HOUR_MS,
    referenceTtlMs: referenceHours * HOUR_MS,
    documentTtlMs: documentHours * HOUR_MS,
    facilityTtlMs: facilityHours * HOUR_MS,
    locationTtlMs: locationHours * HOUR_MS,
    metaTtlMs: metaHours * HOUR_MS,
  };
};

const INITIAL_POLICY = buildPolicy();

export const DEFAULT_OFFLINE_CACHE_POLICY: OfflineCachePolicy = INITIAL_POLICY;

let currentPolicy: OfflineCachePolicy = INITIAL_POLICY;
const listeners = new Set<(policy: OfflineCachePolicy) => void>();

export const getOfflineCachePolicy = (): OfflineCachePolicy => currentPolicy;

export const setOfflineCachePolicy = (hours?: Partial<OfflineCachePolicySettings>) => {
  currentPolicy = buildPolicy(hours);
  listeners.forEach((listener) => listener(currentPolicy));
};

export const subscribeOfflineCachePolicy = (listener: (policy: OfflineCachePolicy) => void): (() => void) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

export const resolvePolicyTtl = (policy: OfflineCachePolicy, key?: OfflineCachePolicyKey): number => {
  if (!key) {
    return policy.defaultTtlMs;
  }
  return policy[key] ?? policy.defaultTtlMs;
};
